/**
 * viewport-fit.ts — 把 #game-container 尺寸綁死到「真正可見區域」,根治手機
 * 全螢幕 overscan(boss 2026-06-13「手機全螢幕時被 overscan 了」)。
 *
 * 問題:iOS standalone / Safari 下,CSS 的 100vh(甚至部分版本的 100dvh)回報的
 * 高度比「實際可見」大(含工具列後方/狀態列後方區域)。Phaser Scale.FIT 以
 * 父容器尺寸計算縮放,容器若比可見區高 → 畫布置中在過高容器裡 → 上下溢出可見
 * 邊界,頂部戰力槽 / 底部血條被裁掉(= overscan)。
 *
 * 修法:不靠 CSS 單位,改用 JS 讀 window.visualViewport(iOS 13+ 支援,回報的是
 * 「視覺視口」= 真正看得到的區域)把容器寬高設成精確 px;容器一變動就
 * scale.refresh() 讓 FIT 在「正確的可見框」內重新 fit(左右留邊,絕不上下溢出)。
 * visualViewport 不可用(舊瀏覽器)→ fallback window.innerWidth/Height。
 *
 * 決策抽純函式 computeVisibleViewport 方便單元測試;mountViewportFit 掛 listener。
 * 鐵則:純尺寸/DOM,0 題目、0 戰鬥數值。
 */

export interface ViewportSize { w: number; h: number; }

/**
 * 算出「真正可見」的視口尺寸(px)。優先 visualViewport(排除工具列等系統 UI),
 * 否則退回 window.innerWidth/Height。回傳整數、最低 1(避免 0 尺寸 canvas)。
 */
export function computeVisibleViewport(
  vv: { width?: number; height?: number } | null | undefined,
  win: { innerWidth?: number; innerHeight?: number },
): ViewportSize {
  const w = Math.max(1, Math.round((vv?.width ?? win.innerWidth ?? 1)));
  const h = Math.max(1, Math.round((vv?.height ?? win.innerHeight ?? 1)));
  return { w, h };
}

type GameLike = { scale?: { refresh?: () => void } } | null | undefined;

/**
 * 把容器尺寸鎖到可見視口,並在視口變化(工具列收合/旋轉/標籤切換)時重算 +
 * Phaser scale.refresh()。回傳卸載函式(移除所有 listener)。Idempotent 由呼叫端
 * 保證(main.ts 掛一次)。所有存取都 try-guard,headless / 無 visualViewport 安全。
 *
 * @param container  #game-container 元素
 * @param getGame    取得當前 Phaser.Game(可能尚未建立 → 回 null,只調容器尺寸)
 */
export function mountViewportFit(
  container: HTMLElement | null | undefined,
  getGame: () => GameLike,
): () => void {
  if (!container) return () => { /* no container (test harness) */ };

  const win = window as unknown as {
    innerWidth?: number; innerHeight?: number;
    visualViewport?: {
      width?: number; height?: number;
      addEventListener?: (t: string, cb: () => void) => void;
      removeEventListener?: (t: string, cb: () => void) => void;
    };
    addEventListener?: (t: string, cb: () => void) => void;
    removeEventListener?: (t: string, cb: () => void) => void;
  };

  const apply = (): void => {
    try {
      const { w, h } = computeVisibleViewport(win.visualViewport, win);
      container.style.width = `${w}px`;
      container.style.height = `${h}px`;
      // 容器尺寸變了 → 讓 Phaser FIT 在新框內重 fit(若 game 已建立)。
      try { getGame()?.scale?.refresh?.(); } catch { /* scene not ready */ }
    } catch { /* defensive */ }
  };

  // orientationchange 後 iOS 的 viewport 尺寸需要一兩拍才穩定 → 延遲再套一次。
  const applySoon = (): void => { apply(); try { window.setTimeout(apply, 250); } catch { /* noop */ } };

  apply();

  const vv = win.visualViewport;
  try {
    vv?.addEventListener?.('resize', apply);
    vv?.addEventListener?.('scroll', apply);
    win.addEventListener?.('resize', apply);
    win.addEventListener?.('orientationchange', applySoon);
  } catch { /* listeners unavailable */ }

  return () => {
    try {
      vv?.removeEventListener?.('resize', apply);
      vv?.removeEventListener?.('scroll', apply);
      win.removeEventListener?.('resize', apply);
      win.removeEventListener?.('orientationchange', applySoon);
    } catch { /* noop */ }
  };
}
